import _isEmpty from 'underscore/cjs/isEmpty.js'

import {
  contentfulFetch,
  removeRichTextInPosts,
  handleRequestSingle,
  generatePostStructure,
  getKey
} from '~/services/contentful/helpers/functions'

export const useExpertAnalysisStore = defineStore('expert-analysis', {
  state: () => {
    return {
      post: {},
      latestNews: {},
      authorPosts: {
        items: []
      }
    }
  },

  actions: {
    async getPost ({ slug, preview } = {}) {
      if (!slug) {
        return {}
      }

      const res = await contentfulFetch('/api/contentful/expert-analysis/get-post', {
        method: 'POST',
        body: {
          limit: 5,
          slug,
          preview
        }
      })

      this.post = res?.post
      this.latestNews = res?.latestNews

      return res ?? {}
    },

    getAuthorPosts ({
      author,
      authorUrl,
      preview = false,
      offset = 0,
      limit = 5,
      slug,
      slugNot,
      tags = []
    } = {}) {
      return useFetch('/api/contentful/expert-analysis/get-author-posts', {
        method: 'post',
        body: {
          ...removeFalseyObj({
            author,
            authorUrl: authorUrl ? `/${authorUrl}` : null,
            slug,
            slugNot,
            tags
          }),
          preview,
          offset,
          limit
        }
      }).then(res => {
        if (!_isEmpty(res?.error.value)) {
          throw(res?.error.value?.data)
        }

        const data = res?.data.value
        const items = data?.items

        this.authorPosts.total = data?.total

        if (!offset) {
          this.authorPosts.items = items
          return data
        }

        if (isObject(items)) {
          this.authorPosts.items.push(items)
        } else {
          this.authorPosts.items.push(...items)
        }

        return data
      }).catch(err => {
        throw(err)
      })
    },

    getPostsByCategory ({ categories = [], limit = 5, preview = false, offset = 0 } = {}) {
      const categoryMap = {
        top_news: 'Top News',
        insurance: 'Insurance',
        loans_and_credit: 'Loans and Credit',
        utilities: 'Utilities',
        savings_and_super: 'Savings and Super',
        life_events: 'Life events'
      }

      return contentfulFetch('/api/contentful/expert-analysis/get-posts-by-category', {
        method: 'POST',
        body: {
          categories,
          limit,
          preview,
          offset
        }
      }).then(res => {
        const data = res ?? {}

        if (_isEmpty(data)) {
          return {}
        }

        for (const key in data) {
          if (Object.hasOwnProperty.call(data, key)) {
            data[key] = (() => {
              const tempData = data[key]

              return {
                title: getKey(key, categoryMap),
                total: tempData?.total || 0,
                ...generatePostStructure({
                  items: tempData.items,
                  hasFeature: !offset
                })
              }
            })()
          }
        }

        return data
        // eslint-disable-next-line no-console
      }).catch(error => console.log(error))
    },

    getCategoryBySlug (slug, preview = false) {
      return contentfulFetch('/api/contentful/expert-analysis/get-category-by-slug', {
        method: 'POST',
        body: {
          slug,
          preview
        }
      }).then(res => {
        res = handleRequestSingle({ data: res }, 'category')

        return res?.data
        // eslint-disable-next-line no-console
      }).catch(error => console.log(error))
    },

    getPostsByAuthor ({ author, slug, limit = 4, preview = false, offset = 0 } = {}) {
      return contentfulFetch('/api/contentful/expert-analysis/get-posts', {
        method: 'POST',
        body: {
          author,
          slug_not: slug,
          limit,
          preview,
          offset
        }
      }).then(res => {
        const posts = res ?? {}

        posts.items = (posts.items || []).map(item => removeRichTextInPosts(item, {
          layout: 'vertical'
        }))

        if (posts.total === 0) {
          return []
        }

        return posts
      })
    },

    getRelatedPosts ({ slug, tags, category, postBySlug = false, preview = false, limit = 4, offset = 0 } = {}) {
      const params = removeFalseyObj({
        slug_not: slug,
        tags,
        category
      })

      if (postBySlug && !_isEmpty(tags)) {
        delete params.category

        params.tags = tags
      }

      return contentfulFetch('/api/contentful/expert-analysis/get-posts', {
        method: 'POST',
        body: {
          ...params,
          limit,
          preview,
          offset
        }
      }).then(res => {
        const posts = res ?? {}

        posts.items = (posts.items || []).map(item => removeRichTextInPosts(item, { layout: 'vertical' }))

        return posts
      }).catch(() => {
        return { items: [], total: 0 }
      })
    }
  }
})
